import React, { useEffect, useMemo, useState } from "react";
import {
    MAX_ZONE_LENGTH_M,
    MAX_ZONE_WIDTH_M,
    MIN_ZONE_DIM_M,
    zoneLengthOutOfRange,
    zoneWidthOutOfRange,
} from "@app/shared/src/helpers/sidePassGeometry";
import { CollisionSensitivity, Device, FleetDefaults, getCollisionSensitivityDisplayName } from "@app/shared/src/models";
import {
    Btn,
    Card,
    CardHead,
    CardNote,
    FRow,
    MPS_TO_MPH,
    Slider,
    Spinner,
    Tag,
    TextInput,
    useToast,
} from "@app/shared/src/components_v2";
import {
    applyRelayPulse,
    applySpeedingLimit,
    applyTriggerEnabled,
    applyTriggerValue,
    relayPulseSeconds,
    useFleetDefaults,
} from "../../hooks/useFleetDefaults";
import { effectiveRelayWiring } from "@app/shared/src/helpers/relayWiring";
import { MAX_SIDE_PASS_LANES, MIN_SIDE_PASS_LANES } from "@app/shared/src/helpers/sidePassGeometry";
import { useFleetDefaultsRecord } from "../../hooks/useFleetDefaultsRecord";
import { SENSITIVITY_FIELD, relayPulseSeconds as triggerRelayPulseSeconds } from "../../helpers/alertSettings";
import { planAdditionalAlertPush, sameMph, sameTenth } from "../../helpers/fleetAlertDefaults";
import { isAttenuator } from "../providers/FleetProvider";
import { ApplyToFleetDialog } from "../shared/ApplyToFleetDialog";
import { Toggle } from "../shared/Toggle";

/**
 * Alerts &amp; Relays — the settings a fleet is meant to run, and the relays that fire them.
 *
 * Every value shown here is the organization's stored default, not an inference from the trucks:
 * that is the whole reason the record exists. Saving records the intent and pushes it to the units
 * that were following the old value, with a confirmation naming them, so the stated default and
 * the fleet can be compared rather than silently conflated.
 */

const SENSITIVITY_STOPS: { key: string; label: string }[] = [
    { key: String(CollisionSensitivity.Lowest), label: "Lowest" },
    { key: String(CollisionSensitivity.Low), label: "Low" },
    { key: String(CollisionSensitivity.Medium), label: "Medium" },
    { key: String(CollisionSensitivity.High), label: "High" },
    { key: String(CollisionSensitivity.Highest), label: "Highest" },
];

type Draft = {
    approachMph: string;
    reactionSeconds: string;
    sensitivity: CollisionSensitivity;
    lightsSeconds: string;
    hornSeconds: string;
    tmaWidthFt: string;
    tmaLengthFt: string;
    speedingEnabled: boolean;
    speedingMph: string;
    sidePassMph: string;
    leftPassEnabled: boolean;
    rightPassEnabled: boolean;
    leftPassLanes: number;
    rightPassLanes: number;
};

/** The zone is drawn in metres; a director measures a truck in feet. */
const M_TO_FT = 3.28084;
const ftRange = (minM: number, maxM: number) => `${(minM * M_TO_FT).toFixed(1)}–${(maxM * M_TO_FT).toFixed(1)} ft`;

function toDraft(d: FleetDefaults, lights?: number, horn?: number): Draft {
    return {
        approachMph: String(Math.round(d.minimumApproachSpeedMps * MPS_TO_MPH)),
        reactionSeconds: d.driverReactionTimeSeconds.toFixed(1),
        sensitivity: d.sensitivity,
        lightsSeconds: (lights ?? d.lightsPulseSeconds).toFixed(1),
        hornSeconds: (horn ?? d.hornPulseSeconds).toFixed(1),
        tmaWidthFt: (d.tmaWidthMetres * M_TO_FT).toFixed(1),
        tmaLengthFt: (d.tmaLengthMetres * M_TO_FT).toFixed(1),
        speedingEnabled: d.speedingEnabled,
        speedingMph: String(Math.round(d.speedingThresholdMps * MPS_TO_MPH)),
        sidePassMph: String(Math.round(d.sidePassMinSpeedMps * MPS_TO_MPH)),
        leftPassEnabled: d.leftPassEnabled,
        rightPassEnabled: d.rightPassEnabled,
        leftPassLanes: d.leftPassLanes,
        rightPassLanes: d.rightPassLanes,
    };
}

export const AlertDefaultsSection: React.FC<{ fleet: ReturnType<typeof useFleetDefaults> }> = ({ fleet }) => {
    const record = useFleetDefaultsRecord();
    const toast = useToast();
    const [draft, setDraft] = useState<Draft>();
    const [pending, setPending] = useState<{ targets: Device[]; untouched: Device[]; run: () => Promise<void> }>();

    const collisionEntries = useMemo(() => fleet.triggerGroups.get("collision") ?? [], [fleet.triggerGroups]);
    // The fleet's stated wiring decides which terminal is "the horn" here. Falls back to the
    // standard build while the record loads; trucks with their own rewired endpoints are the
    // device page's concern.
    const wiring = effectiveRelayWiring(record.defaults);
    const liveLights = relayPulseSeconds(collisionEntries, wiring.lights.number);
    const liveHorn = relayPulseSeconds(collisionEntries, wiring.horn.number);

    useEffect(() => {
        if (record.defaults) setDraft(toDraft(record.defaults, liveLights, liveHorn));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record.defaults, liveLights, liveHorn]);

    if (record.error) {
        return (
            <Card>
                <div className="px-5 py-4 text-[13px] text-[var(--crit)]">{record.error}</div>
            </Card>
        );
    }

    if (record.isLoading || !draft || !record.defaults) {
        return (
            <Card>
                <div className="grid place-items-center p-12 text-[var(--muted)]">
                    <Spinner />
                </div>
            </Card>
        );
    }

    const saved = record.defaults;
    const baseline = toDraft(saved, liveLights, liveHorn);
    const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);

    const parsedApproachMps = Number(draft.approachMph) / MPS_TO_MPH;
    const parsedReaction = Number(draft.reactionSeconds);
    const parsedLights = Number(draft.lightsSeconds);
    const parsedHorn = Number(draft.hornSeconds);
    const parsedSpeedingMps = Number(draft.speedingMph) / MPS_TO_MPH;
    const parsedSidePassMps = Number(draft.sidePassMph) / MPS_TO_MPH;
    const valid =
        Number.isFinite(parsedApproachMps) &&
        Number(draft.approachMph) > 0 &&
        Number.isFinite(parsedReaction) &&
        parsedReaction > 0 &&
        Number.isFinite(parsedLights) &&
        parsedLights > 0 &&
        Number.isFinite(parsedHorn) &&
        parsedHorn > 0 &&
        Number.isFinite(parsedSpeedingMps) &&
        Number(draft.speedingMph) > 0 &&
        Number.isFinite(parsedSidePassMps) &&
        Number(draft.sidePassMph) > 0 &&
        // The TMA footprint sizes a real detection zone, and the layout editor that draws it will
        // not render one outside these bounds.
        !zoneWidthOutOfRange(Number(draft.tmaWidthFt) / M_TO_FT) &&
        !zoneLengthOutOfRange(Number(draft.tmaLengthFt) / M_TO_FT);

    const oldLights = liveLights ?? saved.lightsPulseSeconds;
    const oldHorn = liveHorn ?? saved.hornPulseSeconds;

    // Comparisons run at the precision the screen edits at — whole mph, tenths of a second — never
    // as raw floats. The why (round-trip drift claiming and touching trucks nobody meant to reach)
    // lives with the comparators in helpers/fleetAlertDefaults.
    const speedChanged = !sameMph(parsedApproachMps, saved.minimumApproachSpeedMps);
    const reactionChanged = !sameTenth(parsedReaction, saved.driverReactionTimeSeconds);
    const sensitivityChanged = draft.sensitivity !== saved.sensitivity;
    const lightsChanged = !sameTenth(parsedLights, oldLights);
    const hornChanged = !sameTenth(parsedHorn, oldHorn);

    /*
     * Who this actually reaches.
     *
     * Two rules, and both were being lost. A fleet default governs attenuator units — the trucks
     * this console is about — so a camera or a roadside unit that happens to carry a collision
     * trigger is not a target. And within those, only the ones still sitting on the previously
     * saved value are moved: a truck someone deliberately set differently keeps its own setting,
     * which is the promise the dialog makes in writing.
     *
     * Each field gets its own target list, because they can diverge — a truck can be following the
     * fleet's approach speed while running its own horn pulse — and the confirmation is the union of
     * only the fields that actually changed. The previous version unioned in every collision entry
     * unconditionally, which discarded the filtering directly above it and made the dialog claim the
     * whole fleet on every save.
     */
    const attenuatorEntries = collisionEntries.filter((e) => isAttenuator(e.device));
    const attenuatorsOf = (category: "speeding" | "left" | "right") =>
        (fleet.triggerGroups.get(category) ?? []).filter((e) => isAttenuator(e.device));
    const speedingEntries = attenuatorsOf("speeding");
    const leftEntries = attenuatorsOf("left");
    const rightEntries = attenuatorsOf("right");

    // "Following the default" is judged per exact setting, at display precision — a truck can
    // follow the fleet's approach speed while running its own horn pulse, and only the settings it
    // follows are ever moved.
    const speedTargets = attenuatorEntries.filter((e) => sameMph(e.trigger.additionalValue, saved.minimumApproachSpeedMps));
    const reactionTargets = attenuatorEntries.filter((e) => sameTenth(e.trigger.minValue, saved.driverReactionTimeSeconds));
    // A collision trigger with no stored sensitivity runs the default — readAlertSettings reads it
    // the same way — so "following" includes the legacy null.
    const sensitivityTargets = attenuatorEntries.filter(
        (e) =>
            ((e.trigger[SENSITIVITY_FIELD] as CollisionSensitivity | undefined) ?? saved.sensitivity) === saved.sensitivity,
    );
    const lightsTargets = attenuatorEntries.filter((e) => sameTenth(triggerRelayPulseSeconds(e.trigger, wiring.lights.number), oldLights));
    const hornTargets = attenuatorEntries.filter((e) => sameTenth(triggerRelayPulseSeconds(e.trigger, wiring.horn.number), oldHorn));

    // The additional alerts live on their own triggers. A unit that never had one cannot be moved
    // from here — the device page provisions the zone and trigger on demand — so it simply isn't a
    // target, and the dialog's "untouched" list says so. Which triggers each change reaches is the
    // planner's decision (helpers/fleetAlertDefaults), where the tests hold it still.
    const additionalPlan = planAdditionalAlertPush(
        { speeding: speedingEntries, left: leftEntries, right: rightEntries },
        {
            driverReactionTimeSeconds: saved.driverReactionTimeSeconds,
            speedingEnabled: saved.speedingEnabled,
            speedingThresholdMps: saved.speedingThresholdMps,
            sidePassMinSpeedMps: saved.sidePassMinSpeedMps,
            leftPassEnabled: saved.leftPassEnabled,
            rightPassEnabled: saved.rightPassEnabled,
        },
        {
            driverReactionTimeSeconds: parsedReaction,
            speedingEnabled: draft.speedingEnabled,
            speedingThresholdMps: parsedSpeedingMps,
            sidePassMinSpeedMps: parsedSidePassMps,
            leftPassEnabled: draft.leftPassEnabled,
            rightPassEnabled: draft.rightPassEnabled,
        },
    );

    const affected = Array.from(
        new Set(
            [
                ...(speedChanged ? speedTargets : []),
                ...(reactionChanged ? reactionTargets : []),
                ...(sensitivityChanged ? sensitivityTargets : []),
                ...(lightsChanged ? lightsTargets : []),
                ...(hornChanged ? hornTargets : []),
            ]
                .map((e) => e.device)
                .concat(additionalPlan.devices),
        ),
    );

    /** Attenuators this change deliberately skips, so the dialog can say so rather than stay silent. */
    const untouched = Array.from(
        new Set([...attenuatorEntries, ...speedingEntries, ...leftEntries, ...rightEntries].map((e) => e.device)),
    ).filter((d) => !affected.includes(d));

    /** Whether anything here reaches a truck at all — footprint and lanes are record-only. */
    const touchesFleet =
        speedChanged || reactionChanged || sensitivityChanged || lightsChanged || hornChanged || additionalPlan.writes.length > 0;

    const saveRecord = async () => {
        // Edited in place rather than rebuilt from a spread: the generated client expects
        // Date objects on the record, and a spread would hand it strings.
        saved.minimumApproachSpeedMps = parsedApproachMps;
        saved.driverReactionTimeSeconds = parsedReaction;
        saved.sensitivity = draft.sensitivity;
        saved.lightsPulseSeconds = parsedLights;
        saved.hornPulseSeconds = parsedHorn;
        saved.tmaWidthMetres = Number(draft.tmaWidthFt) / M_TO_FT;
        saved.tmaLengthMetres = Number(draft.tmaLengthFt) / M_TO_FT;
        saved.speedingEnabled = draft.speedingEnabled;
        saved.speedingThresholdMps = parsedSpeedingMps;
        saved.sidePassMinSpeedMps = parsedSidePassMps;
        saved.leftPassEnabled = draft.leftPassEnabled;
        saved.rightPassEnabled = draft.rightPassEnabled;
        saved.leftPassLanes = draft.leftPassLanes;
        saved.rightPassLanes = draft.rightPassLanes;
        await record.save(saved);
    };

    const commit = () => {
        // Nothing here reaches a truck — only the stated default moved. Confirming a fleet-wide push
        // that is not happening would train directors to click through the one dialog that matters.
        if (!touchesFleet) {
            void (async () => {
                await saveRecord();
                toast("Fleet defaults saved");
                fleet.reload();
            })();
            return;
        }

        setPending({
            targets: affected,
            untouched,
            run: async () => {
                // The change flags are computed above, before the record is written: the record is
                // edited in place, so comparing against it afterwards would always say "no change"
                // and nothing would ever reach the trucks.
                await saveRecord();

                // Each push goes to that field's own target list — never to every collision entry.
                // Writing the pulse to all of them is what silently overwrote the trucks a director
                // had deliberately set differently.
                if (speedChanged) await applyTriggerValue(speedTargets, "additionalValue", parsedApproachMps);
                if (reactionChanged) await applyTriggerValue(reactionTargets, "minValue", parsedReaction);
                if (sensitivityChanged) await applyTriggerValue(sensitivityTargets, SENSITIVITY_FIELD, draft.sensitivity);
                if (lightsChanged) await applyRelayPulse(lightsTargets, wiring.lights.number, parsedLights);
                if (hornChanged) await applyRelayPulse(hornTargets, wiring.horn.number, parsedHorn);
                for (const write of additionalPlan.writes) {
                    if (write.set === "enabled") await applyTriggerEnabled(write.targets, write.enabled);
                    else if (write.set === "speedingLimit") await applySpeedingLimit(write.targets, write.value);
                    else await applyTriggerValue(write.targets, write.set, write.value);
                }

                // Units, counted off the target list the dialog just confirmed — not per-trigger
                // writes, which overcount a truck whose change touches both side passes.
                toast(
                    affected.length > 0
                        ? `Defaults saved and pushed to ${affected.length} unit${affected.length === 1 ? "" : "s"}`
                        : "Fleet defaults saved",
                );
                fleet.reload();
            },
        });
    };

    return (
        <>
            <Card>
                <CardHead title="Impending collision" note="the reason the unit is on the truck">
                    <Tag tone="accent">Always on</Tag>
                </CardHead>
                <div className="mt-2">
                    <FRow title="Minimum approach speed" sub="Below this, no alert fires">
                        <span className="flex items-center gap-2">
                            <TextInput
                                value={draft.approachMph}
                                onChange={(e) => setDraft({ ...draft, approachMph: e.target.value })}
                                inputMode="decimal"
                                className="w-[92px] text-right"
                                aria-label="Minimum approach speed in mph"
                            />
                            <span className="text-[13px] text-[var(--muted)]">mph</span>
                        </span>
                    </FRow>
                    <FRow title="Driver reaction time" sub="Lead time added before predicted impact">
                        <span className="flex items-center gap-2">
                            <TextInput
                                value={draft.reactionSeconds}
                                onChange={(e) => setDraft({ ...draft, reactionSeconds: e.target.value })}
                                inputMode="decimal"
                                className="w-[92px] text-right"
                                aria-label="Driver reaction time in seconds"
                            />
                            <span className="text-[13px] text-[var(--muted)]">s</span>
                        </span>
                    </FRow>
                    <FRow
                        stack
                        title="Sensitivity"
                        sub="How long an approaching vehicle is tracked before the alert fires"
                    >
                        <Slider
                            options={SENSITIVITY_STOPS}
                            value={String(draft.sensitivity)}
                            onChange={(v) => setDraft({ ...draft, sensitivity: Number(v) as CollisionSensitivity })}
                            lowLabel="Longer confirmation"
                            highLabel="Earlier warning"
                        />
                    </FRow>
                </div>
                <CardNote>
                    A lower sensitivity watches a target for longer before committing, so the alert is slower but almost never
                    cries wolf. Collision protection itself can never be switched off — not from here, and not from the cab.
                </CardNote>
            </Card>

            <Card>
                <CardHead title="Relays" note="both evaluate on impending collision" />
                <div className="mt-2">
                    {/* Ordered by terminal number, so the rows read the way the wiring is labelled. */}
                    <FRow title={`Relay ${wiring.horn.number} · ${wiring.horn.name}`} sub="Evaluation on · pulse">
                        <span className="flex items-center gap-2">
                            <TextInput
                                value={draft.hornSeconds}
                                onChange={(e) => setDraft({ ...draft, hornSeconds: e.target.value })}
                                inputMode="decimal"
                                className="w-[92px] text-right"
                                aria-label="Horn relay pulse seconds"
                            />
                            <span className="text-[13px] text-[var(--muted)]">s</span>
                        </span>
                    </FRow>
                    <FRow title={`Relay ${wiring.lights.number} · ${wiring.lights.name}`} sub="Evaluation on · pulse">
                        <span className="flex items-center gap-2">
                            <TextInput
                                value={draft.lightsSeconds}
                                onChange={(e) => setDraft({ ...draft, lightsSeconds: e.target.value })}
                                inputMode="decimal"
                                className="w-[92px] text-right"
                                aria-label="Lights relay pulse seconds"
                            />
                            <span className="text-[13px] text-[var(--muted)]">s</span>
                        </span>
                    </FRow>
                </div>
                <CardNote>
                    How long each relay is held closed when the alert fires. Drivers test both from the cab before rolling out.
                </CardNote>
            </Card>

            <Card>
                <CardHead title="Additional alerts" note="what every unit returns to after a restart" />
                <div className="mt-2">
                    <FRow title="Speeding" sub="Vehicle passes over the threshold">
                        <span className="flex items-center gap-2">
                            <TextInput
                                value={draft.speedingMph}
                                onChange={(e) => setDraft({ ...draft, speedingMph: e.target.value })}
                                inputMode="decimal"
                                disabled={!draft.speedingEnabled}
                                className="w-[76px] text-right"
                                aria-label="Fleet speeding threshold in mph"
                            />
                            <span className="text-[13px] text-[var(--muted)]">mph</span>
                            <Toggle
                                on={draft.speedingEnabled}
                                onChange={(v) => setDraft({ ...draft, speedingEnabled: v })}
                                label="Speeding alert fleet default"
                            />
                        </span>
                    </FRow>
                    <FRow title="Side-pass minimum speed" sub="Below this, a passing vehicle raises no alert">
                        <span className="flex items-center gap-2">
                            <TextInput
                                value={draft.sidePassMph}
                                onChange={(e) => setDraft({ ...draft, sidePassMph: e.target.value })}
                                inputMode="decimal"
                                className="w-[76px] text-right"
                                aria-label="Fleet side pass minimum speed in mph"
                            />
                            <span className="text-[13px] text-[var(--muted)]">mph</span>
                        </span>
                    </FRow>
                    {(["left", "right"] as const).map((side) => (
                        <FRow
                            key={side}
                            title={`${side === "left" ? "Left" : "Right"}-side pass`}
                            sub={side === "left" ? "Traffic side of the truck" : "Shoulder side of the truck"}
                        >
                            <span className="flex items-center gap-2">
                                <select
                                    value={side === "left" ? draft.leftPassLanes : draft.rightPassLanes}
                                    disabled={!(side === "left" ? draft.leftPassEnabled : draft.rightPassEnabled)}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            [side === "left" ? "leftPassLanes" : "rightPassLanes"]: Number(e.target.value),
                                        })
                                    }
                                    aria-label={`Fleet ${side} pass width in lanes`}
                                    className="min-w-[104px] rounded-[10px] border border-[var(--line-2)] bg-[var(--surface-2)] px-3 py-2 text-[13.5px] disabled:opacity-40"
                                >
                                    {Array.from({ length: MAX_SIDE_PASS_LANES }, (_, i) => i + MIN_SIDE_PASS_LANES).map((n) => (
                                        <option key={n} value={n}>
                                            {n} lane{n === 1 ? "" : "s"}
                                        </option>
                                    ))}
                                </select>
                                <Toggle
                                    on={side === "left" ? draft.leftPassEnabled : draft.rightPassEnabled}
                                    onChange={(v) =>
                                        setDraft({
                                            ...draft,
                                            [side === "left" ? "leftPassEnabled" : "rightPassEnabled"]: v,
                                        })
                                    }
                                    label={`${side}-side pass fleet default`}
                                />
                            </span>
                        </FRow>
                    ))}
                </div>
                <CardNote>
                    Whether each alert fires; who is emailed when one does lives under{" "}
                    <b className="font-semibold text-[var(--text)]">Notifications</b>. Speeds and switches are pushed to the
                    units still on the old value; a unit that never had the trigger is provisioned from its own settings page.
                    Lane widths are the size a side-pass zone is <i>created</i> at — zones already drawn keep their width.
                </CardNote>
            </Card>

            <Card>
                <CardHead title="Attenuator footprint" note="what a TMA is, fleet-wide" />
                <div className="mt-2">
                    <FRow title="TMA width" sub={`Across the back of the truck · ${ftRange(MIN_ZONE_DIM_M, MAX_ZONE_WIDTH_M)}`}>
                        <span className="flex items-center gap-2">
                            <TextInput
                                value={draft.tmaWidthFt}
                                onChange={(e) => setDraft({ ...draft, tmaWidthFt: e.target.value })}
                                inputMode="decimal"
                                className="w-[92px] text-right"
                                aria-label="TMA width in feet"
                            />
                            <span className="text-[13px] text-[var(--muted)]">ft</span>
                        </span>
                    </FRow>
                    <FRow title="TMA length" sub={`How far the protected area reaches behind the unit · ${ftRange(MIN_ZONE_DIM_M, MAX_ZONE_LENGTH_M)}`}>
                        <span className="flex items-center gap-2">
                            <TextInput
                                value={draft.tmaLengthFt}
                                onChange={(e) => setDraft({ ...draft, tmaLengthFt: e.target.value })}
                                inputMode="decimal"
                                className="w-[92px] text-right"
                                aria-label="TMA length in feet"
                            />
                            <span className="text-[13px] text-[var(--muted)]">ft</span>
                        </span>
                    </FRow>
                </div>
                <CardNote>
                    A truck's protected zone is normally captured when the device is added. This is the size given to a unit
                    that arrived without one, and the length a side-pass zone is measured against. Existing zones are left
                    alone — a footprint someone measured is not something to overwrite with a fleet-wide average.
                </CardNote>
            </Card>

            {/* Sticky save bar — text fields need an explicit commit, and this is a fleet-wide act. */}
            {dirty && (
                <div className="fixed bottom-7 right-7 z-40 flex items-center gap-2.5 rounded-[16px] border border-[var(--line-2)] bg-[var(--surface)] p-3 shadow-[0_20px_50px_rgba(0,0,0,.45)]">
                    <span className="pl-2 pr-1 text-[13.5px] text-[var(--muted)]">Unsaved changes</span>
                    <Btn tone="danger" onClick={() => setDraft(baseline)}>
                        Discard
                    </Btn>
                    <Btn onClick={commit} disabled={!valid}>
                        Save changes
                    </Btn>
                </div>
            )}

            {pending && (
                <ApplyToFleetDialog
                    open
                    onClose={() => setPending(undefined)}
                    title="Save fleet defaults"
                    summary={
                        <>
                            {draft.approachMph} mph · {draft.reactionSeconds} s reaction ·{" "}
                            {getCollisionSensitivityDisplayName(draft.sensitivity) ?? "Medium"} sensitivity · lights{" "}
                            {draft.lightsSeconds} s · horn {draft.hornSeconds} s · speeding{" "}
                            {draft.speedingEnabled ? `over ${draft.speedingMph} mph` : "off"} · left pass{" "}
                            {draft.leftPassEnabled ? "on" : "off"} · right pass {draft.rightPassEnabled ? "on" : "off"}
                        </>
                    }
                    targets={pending.targets}
                    untouched={pending.untouched}
                    onConfirm={async () => {
                        await pending.run();
                        setPending(undefined);
                    }}
                />
            )}
        </>
    );
};

